import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { plan_product_pricings_upload_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

interface PriceLevel {
  id: string
  name: string
}

interface Props {
  slug: string
  channelSlug: string
  channelName: string
  open: boolean
  onClose: () => void
  priceLevels: PriceLevel[]
}

export default function UploadPricingsModal({ slug, channelSlug, channelName, open, onClose, priceLevels }: Props) {
  const market = useMarket()
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const levelSlugs = priceLevels.map((pl) => pl.name.toLowerCase().replace(/\s+/g, '_'))
  const headerLine = ['product_id', ...levelSlugs, 'asp'].join(',')
  const exampleLine = ['13864', ...levelSlugs.map(() => '4965000'), '4070000'].join(',')
  const columnList = [...levelSlugs, 'asp'].join(', ')

  function submit() {
    if (!file) return
    setSubmitting(true)
    const data = new FormData()
    data.append('file', file)
    router.post(plan_product_pricings_upload_path(market.slug, slug, channelSlug), data, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        setFile(null)
        onClose()
      },
      onFinish: () => setSubmitting(false),
    })
  }

  function handleOpenChange(next: boolean) {
    if (!next && !submitting) {
      setFile(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Upload Product Pricings — {channelName}</DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk-update prices and ASP for {channelName}. The file applies to
            this channel only. Columns after the product ID: {columnList}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Expected CSV format</span>
            <p className="text-[12px] text-muted-foreground">
              The first row must be a header. The first column is the product ID; the remaining
              columns are the price levels, then asp. Amounts are in the plan currency, whole or
              decimal, no thousands separators. Leave a cell empty to skip.
            </p>
          </div>

          <div className="overflow-x-auto rounded-md border bg-muted/30 p-3">
            <pre className="text-[11px] leading-relaxed text-foreground tabular-nums">
{headerLine + '\n' + exampleLine}
            </pre>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="pricings-csv" className="text-sm font-medium">CSV file</label>
            <input
              id="pricings-csv"
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={submitting}
              className="text-[13px] file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-foreground hover:file:bg-accent"
            />
            {file && (
              <span className="text-[11px] text-muted-foreground">
                Selected: {file.name} ({Math.round(file.size / 1024)} KB)
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!file || submitting}>
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
